import { Card, CardBody } from "@progress/kendo-react-layout";
import { SovraSkeleton } from "../../../../components/skeleton/sovraSkeleton";
import useCurrentPlan from "../../../../hooks/useCurrentPlan";
import type { CommercialSimulatorAgentResponse } from "../../../../models/agentResponseModels";
import type { PlanByPeriodItem } from "../../../../models/commericalPlanModels";
import { formatDecimal, formatSignedPercentChange, formatToScaledRoundedThousands, sumNullable } from "../../../../utils/numberValueConversion";

type VolumeDriverMetric = {
    id: number;
    label: string;
    previousValue?: string;
    nextValue?: string;
    percentChange?: string;
};

type VolumeDriversSummaryProps = {
    isLoading?: boolean;
};

enum VolumeDriversEnum {
    Stores,
    Velocity
}

const initVolumeDriverMetrics: VolumeDriverMetric[] = [
    {
        id: VolumeDriversEnum.Stores,
        label: "Stores",
        previousValue: "820",
        nextValue: "860",
        percentChange: "(+5%)"
    },
    {
        id: VolumeDriversEnum.Velocity,
        label: "Forecast Units",
        previousValue: "12.4",
        nextValue: "14.1",
        percentChange: "(+13%)"
    }
];

const getStoresMetrics = (periods: PlanByPeriodItem[]): VolumeDriverMetric => {
    const averageStores
        = (sumNullable(periods.map(period => period.number_stores) ?? []) ?? 0) / periods.length;

    return {
        id: VolumeDriversEnum.Stores,
        label: initVolumeDriverMetrics[VolumeDriversEnum.Stores].label,
        previousValue: formatDecimal(averageStores, 0)
    };
};

const getVelocityMetrics = (periods: PlanByPeriodItem[]): VolumeDriverMetric => {
    const totalBaseVelocity
        = (sumNullable(periods.map(period => (period.period_base_velocity ?? 0) * (period.number_stores ?? 1)) ?? []) ?? 0);
    const totalExpectedVelocity
        = (sumNullable(periods.map(period => (period.period_base_velocity ?? 0) * (period.number_stores ?? 1) * (1 + (period.period_total_velocity_change ?? 0))) ?? []) ?? 0);

    return {
        id: VolumeDriversEnum.Velocity,
        label: initVolumeDriverMetrics[VolumeDriversEnum.Velocity].label,
        previousValue: formatToScaledRoundedThousands(totalBaseVelocity),
        nextValue: formatToScaledRoundedThousands(totalExpectedVelocity),
        percentChange: formatSignedPercentChange(totalBaseVelocity, totalExpectedVelocity)
    };
};

const getVolumeDrivers = (currentPlan: CommercialSimulatorAgentResponse | null): VolumeDriverMetric[] => {
    const periods = currentPlan?.plan_by_retailer?.[0]?.plan_by_period;
    if (!periods?.length) {
        return initVolumeDriverMetrics.map(metric => ({ ...metric }));
    }

    return initVolumeDriverMetrics.map(metric => {
        switch (metric.id) {
            case VolumeDriversEnum.Stores:
                return getStoresMetrics(periods);

            case VolumeDriversEnum.Velocity:
                return getVelocityMetrics(periods);

            default:
                return { ...metric };
        }
    });
};

const renderVolumeDriversSummarySkeleton = (): React.ReactNode => (
    <Card
        className="border rounded-3 bg-light-subtle shadow-sm w-100 mt-1"
        style={{ boxShadow: "0 2px 8px rgba(13, 110, 253, 0.08)" }}
    >
        <CardBody className="px-3 px-md-4 py-2">
            <div className="d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-2 gap-md-4">
                <SovraSkeleton
                    shape="text"
                    style={{ width: 118, height: 20 }}
                />

                <div className="d-flex flex-wrap align-items-center justify-content-start justify-content-md-end gap-3 gap-lg-4">
                    {Array.from({ length: 3 }, (_, index) => (
                        <div key={index} className="d-flex align-items-center gap-2 lh-sm flex-wrap">
                            <SovraSkeleton
                                shape="text"
                                style={{ width: index === 2 ? 102 : 64, height: 18 }}
                            />

                            {index < 2 && (
                                <SovraSkeleton
                                    shape="text"
                                    style={{ width: 78, height: 18 }}
                                />
                            )}

                            <SovraSkeleton
                                shape="rectangle"
                                style={{ width: 42, height: 22, borderRadius: 999 }}
                            />
                        </div>
                    ))}
                </div>
            </div>
        </CardBody>
    </Card>
);

const VolumeDriversSummary: React.FC<VolumeDriversSummaryProps> = ({ isLoading }) => {
    const { currentPlan } = useCurrentPlan();

    if (isLoading) {
        return renderVolumeDriversSummarySkeleton();
    }

    const volumeDrivers = getVolumeDrivers(currentPlan);

    return (
        <Card className="border rounded-3 bg-light-subtle shadow-sm w-100 min-w-0 shadow-sm">
            <CardBody className="px-md-4 gap-3 volume-driver min-w-0">
                <div className="d-flex flex-wrap align-items-center gap-3 min-w-0">
                    <div
                        className="k-font-size-xs fw-bold text-uppercase flex-shrink-0 k-text-primary"
                        style={{ letterSpacing: "0.075em" }}
                    >
                        Volume drivers
                    </div>

                    <div className="d-flex flex-wrap align-items-center justify-content-start gap-3 gap-lg-4 min-w-0 flex-grow-1">
                        {volumeDrivers.map(metric => (
                            <div key={metric.label} className="d-flex align-items-center gap-2 lh-sm flex-wrap">
                                <span className="small fw-medium k-text-subtle" style={{ color: "oklch(0.55 0.05 257.55)" }}>{`${metric.label}:`}</span>

                                {metric.previousValue && (
                                    <span className="d-inline-flex align-items-center gap-2 text-body">
                                        <span className="k-text-subtle fs-7">{metric.previousValue}</span>
                                        {metric.nextValue && (
                                            <>
                                                <span className="text-secondary fs-7">→</span>
                                                <span className="fw-semibold fs-7 k-text-primary">{metric.nextValue}</span>
                                            </>
                                        )}
                                    </span>
                                )}

                                {metric.percentChange && <span className="fw-semibold k-text-success fs-7">{metric.percentChange}</span>}
                            </div>
                        ))}
                    </div>
                </div>
            </CardBody>
        </Card>
    );
};

export default VolumeDriversSummary;
